/**
 * ChatPanel — floating AI asistent
 * Dostupný na každé stránce přes floating button (vpravo dole).
 * Komunikuje s /apiext/chat/stream přes SSE.
 * Zobrazuje tool cally a markdown odpovědi.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { openUiChatStream } from '../lib/chat-stream';
import {
  Bot,
  Check,
  Copy,
  Loader2,
  MessageCircle,
  Send,
  Square,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { normalizeLang, type Lang } from '../lib/i18n';
import { mcpCall } from '../lib/mcp';


const CHAT_LANG_LABEL: Record<Lang, string> = {
  cs: 'čeština',
  en: 'English',
  it: 'italiano',
  cvm: 'Caveman',
};

function chatPreferredLang(raw: unknown): Lang {
  return normalizeLang(raw);
}

type Role = 'user' | 'assistant';

/**
 * Kdo v chatu mluví (Filip 2026-11-02): výchozí Kamila, `/janet`, `/badjanet` a
 * `/neutraljanet` přepnou na Janet, Bad Janet a Neutral Janet z The Good Place,
 * `/kamila` vrátí zpátky.
 * Volba se pamatuje v prohlížeči; server podle ní vymění identitu promptu.
 */
type Persona = 'kamila' | 'janet' | 'bad_janet' | 'neutral_janet';
const PERSONA_COMMANDS: Record<string, Persona> = {
  '/kamila': 'kamila',
  '/janet': 'janet',
  '/badjanet': 'bad_janet',
  '/neutraljanet': 'neutral_janet',
};
const PERSONA_NAMES: Record<Exclude<Persona, 'kamila'>, string> = {
  janet: 'Janet',
  bad_janet: 'Bad Janet',
  neutral_janet: 'Neutral Janet',
};
const PERSONA_STORAGE_KEY = 'acm_chat_persona';

function readPersona(): Persona {
  try {
    const v = window.localStorage.getItem(PERSONA_STORAGE_KEY);
    return v === 'janet' || v === 'bad_janet' || v === 'neutral_janet' ? v : 'kamila';
  } catch {
    return 'kamila';
  }
}

interface ToolCallItem {
  name: string;
  result?: unknown;
  // Pro superagent_run — extrahovaný výsledek a session ID
  superagentResult?: string;
  sessionId?: string;
}

interface Message {
  id: string;
  role: Role;
  content: string;
  toolCalls?: ToolCallItem[];
  pending?: boolean;
  /** Model přemýšlí (reasoning stream) — indikátor místo zamrzlé bubliny. */
  thinking?: boolean;
  /** Info eventy z backendu (fallback profilu, denní limit, skryté tooly) —
   * dřív se zahazovaly a přepnutí modelu bylo neviditelné. */
  infos?: string[];
  /** Hláška panelu (přepnutí persony) — modelu se neposílá. */
  local?: boolean;
}

function useChatEnabled() {
  return useQuery({
    queryKey: ['chat-config'],
    queryFn: () => mcpCall<{ enabled: string }>('chat.config_get', {}, { silent: true }),
    staleTime: 30_000,
  });
}

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function clampNum(v: number, min: number, max: number) {
  return Math.min(Math.max(v, min), Math.max(min, max));
}

/**
 * Draggable floating element — pozice jako offset od bottom-right rohu,
 * persist v localStorage, clamp do viewportu (i při resize), pointer events
 * (= funguje mouse i touch; element potřebuje třídu `touch-none`).
 */
function useDraggable(storageKey: string, def: { x: number; y: number }) {
  const elRef = useRef<HTMLElement | null>(null);
  const movedRef = useRef(false);
  const [pos, setPos] = useState<{ x: number; y: number }>(() => {
    if (typeof window === 'undefined') return def;
    try {
      const saved = window.localStorage.getItem(storageKey);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return def;
  });

  // Clamp při mountu (uložená pozice může být mimo menší okno) a při resize
  useEffect(() => {
    const clampToViewport = () =>
      setPos((p) => {
        const w = elRef.current?.offsetWidth ?? 120;
        const h = elRef.current?.offsetHeight ?? 44;
        return {
          x: clampNum(p.x, 8, window.innerWidth - w - 8),
          y: clampNum(p.y, 8, window.innerHeight - h - 8),
        };
      });
    clampToViewport();
    window.addEventListener('resize', clampToViewport);
    return () => window.removeEventListener('resize', clampToViewport);
  }, []);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    movedRef.current = false;
    const start = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y };
    const w = elRef.current?.offsetWidth ?? 120;
    const h = elRef.current?.offsetHeight ?? 44;
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - start.x;
      const dy = ev.clientY - start.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) movedRef.current = true;
      // Offset od bottom-right → drag doprava/dolů offset snižuje
      setPos({
        x: clampNum(start.px - dx, 8, window.innerWidth - w - 8),
        y: clampNum(start.py - dy, 8, window.innerHeight - h - 8),
      });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      setPos((p) => {
        try {
          window.localStorage.setItem(storageKey, JSON.stringify(p));
        } catch { /* ignore */ }
        return p;
      });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }, [pos, storageKey]);

  return { pos, setPos, elRef, movedRef, onPointerDown };
}

function AssistantBubble({ content }: { content: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className='relative group'>
      <div className='prose prose-invert prose-sm max-w-none px-4 py-2.5
        prose-p:my-1 prose-p:leading-relaxed prose-p:break-words
        prose-pre:bg-gray-900 prose-pre:text-xs prose-pre:overflow-x-auto prose-pre:whitespace-pre-wrap
        prose-code:text-blue-300 prose-code:break-all
        prose-li:my-0.5 prose-ul:my-1 prose-ol:my-1
        [&_*]:max-w-full [&_*]:break-words'>
        <ReactMarkdown>{content}</ReactMarkdown>
      </div>
      <button
        onClick={copy}
        className='absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-white'
        title={t('chat.copy', 'Kopírovat')}
      >
        {copied ? <Check size={11} className='text-green-400' /> : <Copy size={11} />}
      </button>
    </div>
  );
}

export default function ChatPanel() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { data: config } = useChatEnabled();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [persona, setPersona] = useState<Persona>(readPersona);
  const personaName = persona === 'kamila' ? t('chat.title', 'Kamila') : PERSONA_NAMES[persona];
  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Draggable switch — Filip 2026-07-08: "switch nesmí překážet, musí být
  // přesouvatelný kamkoli". Okno chatu je plovoucí taky (drag za header)
  // a při prvním otevření se ukotví k přepínači.
  const switchDrag = useDraggable('acm_chat_switch_pos', { x: 24, y: 24 });
  const windowDrag = useDraggable('acm_chat_window_pos', { x: 24, y: 84 });

  const toggleOpen = useCallback(() => {
    setOpen((o) => {
      if (!o && !window.localStorage.getItem('acm_chat_window_pos')) {
        // První otevření bez uložené pozice → ukotvi okno nad přepínač
        const w = Math.min(680, window.innerWidth - 16);
        const h = Math.min(560, window.innerHeight - 16);
        windowDrag.setPos({
          x: clampNum(switchDrag.pos.x, 8, window.innerWidth - w - 8),
          y: clampNum(switchDrag.pos.y + 56, 8, window.innerHeight - h - 8),
        });
      }
      return !o;
    });
  }, [switchDrag.pos, windowDrag]);

  const onHeaderPointerDown = useCallback((e: React.PointerEvent) => {
    // Kliky na tlačítka v headeru nesmí začít drag
    if ((e.target as HTMLElement).closest('button')) return;
    windowDrag.onPointerDown(e);
  }, [windowDrag]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const switchTo = PERSONA_COMMANDS[text.toLowerCase()];
    if (switchTo) {
      setPersona(switchTo);
      try {
        window.localStorage.setItem(PERSONA_STORAGE_KEY, switchTo);
      } catch { /* bez úložiště platí volba do zavření stránky */ }
      const name = switchTo === 'kamila' ? t('chat.title', 'Kamila') : PERSONA_NAMES[switchTo];
      setInput('');
      setMessages((prev) => [...prev, {
        id: uid(),
        role: 'assistant',
        content: t('chat.personaSwitched', 'Teď mluví {{name}}. Začíná nová konverzace.', { name }),
        local: true,
      }]);
      return;
    }

    const userMsg: Message = { id: uid(), role: 'user', content: text };
    const assistantMsg: Message = {
      id: uid(),
      role: 'assistant',
      content: '',
      toolCalls: [],
      pending: true,
    };

    setMessages((prev) => [...prev, userMsg, assistantMsg]);
    setInput('');
    setStreaming(true);

    const preferredLang = chatPreferredLang(i18n.language);
    const langName = t(`chat.lang.${preferredLang}`, CHAT_LANG_LABEL[preferredLang]);
    const languageSystem = {
      role: 'system',
      content:
        `preferred_language: ${preferredLang} (${langName}). ` +
        (preferredLang === 'cvm'
          ? `Caveman styl: odpovídej krátkou hravou česko-primitivní řečí, např. "Já kouk.", "Kámen bolí.", "Udělat.", "Oheň dobrý.". `
          : '') +
        `Odpovídej primárně tímto jazykem UI; technické názvy, kód, CLI příkazy, API a logy nech v původním technickém jazyce. ` +
        `Pokud uživatel v aktuální zprávě požádá o jiný jazyk, jeho pokyn má přednost.`,
    };
    // Přepnutí persony je hranice konverzace (Filip 2026-11-03): starší odpovědi
    // jiným hlasem by novou identitu přebily („už má otrávený kontext"). Model
    // dostane jen to, co bylo napsáno po posledním přepnutí; okno zobrazí vše.
    let lastSwitch = -1;
    messages.forEach((m, i) => {
      if (m.local) lastSwitch = i;
    });
    const sinceSwitch = messages.slice(lastSwitch + 1);
    const history = [languageSystem, ...sinceSwitch, userMsg]
      .map((m) => ({
        role: m.role,
        content: m.content,
      }));

    abortRef.current = new AbortController();

    try {
      // Same-origin capability (/ui/capabilities/chat-stream → proxy → API).
      // Přímé volání MCP hostu (<hash>.acm-mcp.<base>) je jiný HOST a host-only
      // cookie acm_session tam prohlížeč nepošle → 401 na každé publikované
      // doméně (MAXACM000 2026-09-06). Interně to fungovalo jen proto, že
      // cookie ignoruje port (:9080 vs :9082).
      const resp = await openUiChatStream(
        { messages: history, persona },
        abortRef.current.signal,
        {
          error: t('common.sessionExpired', 'Přihlášení vypršelo; obnovuji autorizaci.'),
          remediation: t(
            'common.sessionExpiredHint',
            'Po obnovení autorizace budete vráceni na tuto stránku.',
          ),
        },
      );

      if (!resp.ok) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? {
                ...m,
                content: t('chat.error', 'Chyba: {{message}}', {
                  message: `${resp.status} ${resp.statusText}`,
                }),
                pending: false,
              }
              : m
          )
        );
        return;
      }

      const reader = resp.body!.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let thinkingShown = false; // flip jen při změně, ne každý delta

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const raw = line.slice(6).trim();
          if (raw === '[DONE]') continue;
          try {
            const ev = JSON.parse(raw) as {
              type: 'text' | 'tool_call' | 'tool_result' | 'error' | 'thinking' | 'info';
              text?: string;
              name?: string;
              result?: unknown;
              message?: string;
            };

            if (ev.type === 'text') {
              thinkingShown = false;
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsg.id
                    ? { ...m, content: m.content + ev.text, thinking: false }
                    : m
                )
              );
            } else if (ev.type === 'thinking') {
              if (!thinkingShown) {
                thinkingShown = true;
                setMessages((prev) =>
                  prev.map((m) => m.id === assistantMsg.id ? { ...m, thinking: true } : m)
                );
              }
            } else if (ev.type === 'info') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsg.id
                    ? { ...m, infos: [...(m.infos ?? []), ev.message ?? ''] }
                    : m
                )
              );
            } else if (ev.type === 'tool_call') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsg.id
                    ? { ...m, toolCalls: [...(m.toolCalls ?? []), { name: ev.name! }] }
                    : m
                )
              );
            } else if (ev.type === 'tool_result') {
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantMsg.id) return m;
                  const result = ev.result as Record<string, unknown> | undefined;
                  return {
                    ...m,
                    toolCalls: (m.toolCalls ?? []).map((tc) =>
                      tc.name === ev.name && tc.result === undefined
                        ? {
                          ...tc,
                          result: ev.result,
                          superagentResult: tc.name === 'superagent_run'
                            ? (typeof result?.result === 'string' ? result.result : undefined)
                            : undefined,
                          sessionId: tc.name === 'superagent_run'
                            ? (typeof result?.session_id === 'string'
                              ? result.session_id
                              : undefined)
                            : undefined,
                        }
                        : tc
                    ),
                  };
                })
              );
            } else if (ev.type === 'error') {
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantMsg.id
                    ? { ...m, content: m.content + `\n\n⚠️ ${ev.message}` }
                    : m
                )
              );
            }
          } catch { /* skip */ }
        }
      }
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantMsg.id
              ? {
                ...m,
                content: t('chat.connectionError', 'Chyba připojení: {{message}}', {
                  message: (e as Error).message,
                }),
                pending: false,
              }
              : m
          )
        );
      }
    } finally {
      setMessages((prev) =>
        prev.map((m) => m.id === assistantMsg.id ? { ...m, pending: false } : m)
      );
      setStreaming(false);
    }
  }, [input, messages, streaming, i18n.language, persona]);

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Early return až po všech hookách.
  // config === null = chat modul není načtený (např. .disabled → /apiext/chat/config vrátí 404).
  // V takovém případě floating button nesmí být viditelný — modul nemá běžící backend ani settings.
  if (!config || config.enabled === 'false') return null;
  // Filip 2026-07-08: base ACM chat je globální — na všech stránkách včetně Codable.
  // Switch je draggable (řízeno níže) aby nepřekážel — user si ho přesune kamkoli.
  void location;

  return (
    <>
      {/* Toggle přepínač — draggable (pointer events, funguje i touch).
          Click otevře/zavře chat, drag přemístí switch. Persist v localStorage. */}
      <button
        ref={(el) => {
          switchDrag.elRef.current = el;
        }}
        onPointerDown={switchDrag.onPointerDown}
        onClick={(e) => {
          // Pokud switch byl dragován, potlač click (aby drag neotevřel chat)
          if (switchDrag.movedRef.current) {
            e.preventDefault();
            switchDrag.movedRef.current = false;
            return;
          }
          toggleOpen();
        }}
        style={{ bottom: switchDrag.pos.y, right: switchDrag.pos.x }}
        className='fixed z-50 flex items-center gap-2.5 px-4 h-11 rounded-full shadow-xl transition-colors bg-gray-900/90 backdrop-blur-sm hover:bg-gray-800 border border-gray-700 hover:border-gray-500 select-none touch-none cursor-grab active:cursor-grabbing'
        title={open ? t('chat.closeTitle', 'Zavřít chat (tažením přemístíš)') : t('chat.openTitle', 'Otevřít chat (tažením přemístíš)')}
      >
        <MessageCircle size={16} className={open ? 'text-blue-400' : 'text-gray-500'} />
        <span
          className={`text-sm font-medium transition-colors ${
            open ? 'text-white' : 'text-gray-400'
          }`}
        >
          Chat
        </span>
        {/* Toggle track */}
        <div
          style={{
            backgroundColor: open ? '#2563eb' : '#374151',
            transition: 'background-color 0.2s',
          }}
          className='relative w-10 h-6 rounded-full flex-shrink-0'
        >
          <div
            style={{
              transform: open ? 'translateX(18px)' : 'translateX(2px)',
              transition: 'transform 0.2s',
              top: '3px',
            }}
            className='absolute w-[18px] h-[18px] rounded-full bg-white shadow-md'
          />
        </div>
        {messages.length > 0 && !open && (
          <span className='absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full text-[9px] font-bold flex items-center justify-center text-white'>
            {messages.filter((m) => m.role === 'assistant').length}
          </span>
        )}
      </button>

      {/* Chat panel — plovoucí okno, drag za header, pozice persist */}
      {open && (
        <div
          ref={(el) => {
            windowDrag.elRef.current = el;
          }}
          style={{ bottom: windowDrag.pos.y, right: windowDrag.pos.x }}
          className='fixed z-50 w-[min(680px,calc(100vw-1rem))] max-h-[min(80vh,calc(100vh-1rem))] min-h-[min(520px,calc(100vh-1rem))] bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl flex flex-col overflow-hidden'
        >
          {/* Header = drag handle */}
          <div
            onPointerDown={onHeaderPointerDown}
            className='flex items-center gap-3 px-4 py-3 border-b border-gray-800 bg-gray-900/80 select-none touch-none cursor-grab active:cursor-grabbing'
            title={t('chat.dragTitle', 'Tažením přemístíš okno')}
          >
            <MessageCircle size={15} className='text-blue-400' />
            <span className='text-sm font-semibold text-white flex-1'>
              {personaName}
            </span>
            <button
              onClick={() => {
                setMessages([]);
              }}
              className='text-gray-500 hover:text-gray-300 p-1 rounded'
              title={t('chat.clear', 'Vymazat historii')}
            >
              <Trash2 size={13} />
            </button>
            <button
              onClick={() => setOpen(false)}
              className='text-gray-500 hover:text-gray-300 p-1 rounded'
              title={t('chat.close', 'Zavřít')}
            >
              <X size={15} />
            </button>
          </div>

          {/* Messages */}
          <div className='flex-1 overflow-y-auto p-4 space-y-4 min-h-0'>
            {messages.length === 0 && (
              <div className='text-center text-gray-500 text-sm py-8'>
                <MessageCircle size={28} className='mx-auto mb-2 opacity-30' />
                <p>{t('chat.emptyTitle', 'Jak mohu pomoci?')}</p>
                <p className='text-xs mt-1 opacity-60'>
                  {t('chat.emptyHint', 'Ptej se na stav infrastruktury, moduly, kontejnery...')}
                </p>
              </div>
            )}
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex flex-col gap-1 ${
                  msg.role === 'user' ? 'items-end' : 'items-start'
                }`}
              >
                {/* Tool calls */}
                {msg.toolCalls && msg.toolCalls.length > 0 && (
                  <div className='flex flex-wrap gap-1 max-w-[85%]'>
                    {msg.toolCalls.map((tc, i) => (
                      <span
                        key={i}
                        className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-mono ${
                          tc.result !== undefined
                            ? tc.name === 'superagent_run'
                              ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20'
                              : 'bg-green-500/10 text-green-400 border border-green-500/20'
                            : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                        }`}
                      >
                        {tc.name === 'superagent_run' ? <Bot size={9} /> : <Wrench size={9} />}
                        {tc.name}
                        {tc.result === undefined && <Loader2 size={9} className='animate-spin' />}
                      </span>
                    ))}
                  </div>
                )}

                {/* Superagent výsledky — speciální bublina pro každý superagent_run výsledek */}
                {msg.toolCalls?.filter((tc) => tc.name === 'superagent_run' && tc.superagentResult)
                  .map((tc, i) => (
                    <div
                      key={i}
                      className='max-w-[90%] rounded-2xl rounded-bl-sm border border-purple-500/25 bg-purple-900/10 overflow-hidden'
                    >
                      <div className='flex items-center gap-1.5 px-3 py-2 border-b border-purple-500/15 bg-purple-900/15'>
                        <Bot size={11} className='text-purple-400 flex-shrink-0' />
                        <span className='text-[11px] font-semibold text-purple-300'>
                          Superagent
                        </span>
                        {tc.sessionId && (
                          <span className='text-[10px] text-gray-600 font-mono ml-1'>
                            #{tc.sessionId.slice(-8)}
                          </span>
                        )}
                      </div>
                      <div className='prose prose-invert prose-sm max-w-none px-4 py-2.5
                      prose-p:my-1 prose-p:leading-relaxed prose-p:break-words
                      prose-pre:bg-gray-900 prose-pre:text-xs prose-pre:overflow-x-auto prose-pre:whitespace-pre-wrap
                      prose-code:text-purple-300 prose-code:break-all
                      prose-li:my-0.5 prose-ul:my-1 prose-ol:my-1
                      [&_*]:max-w-full [&_*]:break-words'>
                        <ReactMarkdown>{tc.superagentResult!}</ReactMarkdown>
                      </div>
                    </div>
                  ))}

                {/* Bubble */}
                <div
                  className={`max-w-[90%] w-full rounded-2xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-sm px-4 py-2.5'
                      : 'bg-gray-800 text-gray-100 rounded-bl-sm'
                  }`}
                >
                  {msg.role === 'assistant'
                    ? (
                      msg.pending && !msg.content
                        ? (
                          <>
                            {(msg.infos ?? []).map((inf, i) => (
                              <div key={i} className='px-4 pt-2 text-[11px] text-amber-400/80 italic'>
                                ℹ️ {inf}
                              </div>
                            ))}
                            <span className='flex items-center gap-2 text-gray-400 px-4 py-2.5'>
                              <Loader2 size={13} className='animate-spin' />{' '}
                              {t('chat.thinking', 'Přemýšlím...')}
                            </span>
                          </>
                        )
                        : (
                          <>
                            {(msg.infos ?? []).map((inf, i) => (
                              <div key={i} className='px-4 pt-2 text-[11px] text-amber-400/80 italic'>
                                ℹ️ {inf}
                              </div>
                            ))}
                            <AssistantBubble content={msg.content} />
                            {msg.pending && msg.thinking && (
                              <div className='px-4 pb-2 flex items-center gap-1.5 text-[11px] text-gray-500 italic'>
                                <Loader2 size={11} className='animate-spin' />
                                {t('chat.thinkingMore', 'přemýšlí…')}
                              </div>
                            )}
                          </>
                        )
                    )
                    : <span className='whitespace-pre-wrap break-words'>{msg.content}</span>}
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className='p-3 border-t border-gray-800 bg-gray-900'>
            <div className='flex gap-2 items-end'>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                placeholder={t(
                  'chat.placeholder',
                  'Napiš dotaz... (Enter = odeslat, Shift+Enter = nový řádek)',
                )}
                rows={1}
                disabled={streaming}
                className='flex-1 bg-gray-800 text-white text-sm rounded-xl px-3 py-2.5 resize-none outline-none border border-gray-700 focus:border-blue-500 transition-colors placeholder-gray-500 max-h-32 overflow-y-auto disabled:opacity-50'
                style={{ minHeight: '40px' }}
                onInput={(e) => {
                  const t = e.currentTarget;
                  t.style.height = 'auto';
                  t.style.height = Math.min(t.scrollHeight, 128) + 'px';
                }}
              />
              {streaming
                ? (
                  <button
                    onClick={() => abortRef.current?.abort()}
                    className='w-9 h-9 flex-shrink-0 bg-red-600 hover:bg-red-500 rounded-xl flex items-center justify-center transition-colors'
                    title={t('chat.stop', 'Zastavit odpověď')}
                  >
                    <Square size={14} className='text-white fill-white' />
                  </button>
                )
                : (
                  <button
                    onClick={send}
                    disabled={!input.trim()}
                    className='w-9 h-9 flex-shrink-0 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl flex items-center justify-center transition-colors'
                  >
                    <Send size={15} className='text-white' />
                  </button>
                )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
